import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, User } from "lucide-react";
import { Link } from "react-router-dom";
import ScrollReveal from "@/components/ScrollReveal";

const Blogue = () => {
  const { data: blogPosts, isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  return (
    <>
      <Helmet>
        <title>Blogue - Agroref</title>
        <meta name="description" content="Découvrez nos derniers articles et actualités sur les équipements agricoles et la traite laitière." />
      </Helmet>

      <div className="-mx-4 -mt-16">
        {/* Hero Section */}
        <section className="bg-[#2c2c2c] text-white py-16 md:py-20 relative">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10 md:mb-12">
              <h1 className="text-3xl md:text-5xl font-bold mb-4">Blogue</h1>
              <div className="w-32 md:w-48 h-0.5 bg-accent mx-auto mb-6"></div>
              <p className="text-base md:text-lg text-white">
                Restez informé des dernières actualités et conseils
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 -mb-px">
            <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto" preserveAspectRatio="none">
              <path d="M0 0C240 53.3333 480 80 720 80C960 80 1200 53.3333 1440 0V80H0V0Z" fill="white"/>
            </svg>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="bg-white py-16">
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Chargement des articles...</p>
              </div>
            ) : blogPosts && blogPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {blogPosts.map((post, index) => (
                  <ScrollReveal key={post.id} delay={index * 0.1}>
                    <Link to={`/blogue/${post.slug}`}>
                      <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer overflow-hidden">
                        {post.image_url && (
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={post.image_url}
                              alt={post.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                        )}
                        <CardHeader className="pb-2">
                          <div className="mb-2">
                            <span className="inline-block bg-destructive text-white text-xs font-bold px-3 py-1 rounded">
                              AGRO
                            </span>
                          </div>
                          <CardTitle className="line-clamp-2 text-lg">{post.title}</CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              <span>Temps de lecture: {(post as any).reading_time || 5} minutes</span>
                            </div>
                          </div>
                          {post.excerpt && (
                            <p className="text-muted-foreground line-clamp-2 text-sm">{post.excerpt}</p>
                          )}
                        </CardContent>
                      </Card>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Aucun article publié pour le moment.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Blogue;
